import type { CSSProperties, ReactNode } from 'react'
import {
  agendaInfo,
  ehVariosDias,
  faixaDeHorario,
  parseDayKey,
  situacaoDoItem,
  ultimoDia,
  type Compromisso,
  type EventoDaApi,
} from './calendario.ts'

const DIA_FMT = new Intl.DateTimeFormat('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })
const DIA_CURTO_FMT = new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'short' })

const capitalizar = (texto: string) => texto.charAt(0).toUpperCase() + texto.slice(1)

/** "Quarta-feira, 28 de outubro · 09:00 – 10:00" (ou o intervalo de vários dias). */
export function linhaDeQuando(c: Pick<Compromisso, 'date' | 'endDate' | 'startTime' | 'endTime'>): string {
  const horario = faixaDeHorario(c).replace(' — ', ' – ')
  if (!ehVariosDias(c)) return `${capitalizar(DIA_FMT.format(parseDayKey(c.date)))} · ${horario}`
  const de = DIA_CURTO_FMT.format(parseDayKey(c.date))
  const ate = DIA_CURTO_FMT.format(parseDayKey(ultimoDia(c)))
  return c.startTime === null ? `${de} a ${ate} · Dia inteiro` : `${de}, ${c.startTime} até ${ate}${c.endTime ? `, ${c.endTime}` : ''}`
}

const ehGoogleMeet = (url: string) => /meet\.google\.com/i.test(url)

/** Linha do detalhe: ícone numa coluna fixa e o conteúdo ao lado (design). */
function Linha({ icone, children, alto = false }: { icone: string; children: ReactNode; alto?: boolean }) {
  return (
    <div className={`cal-sheet__linha${alto ? ' cal-sheet__linha--alto' : ''}`}>
      <span className="cal-sheet__icone" aria-hidden>
        <i className={icone} />
      </span>
      <div className="cal-sheet__valor">{children}</div>
    </div>
  )
}

function Acao({ icone, rotulo, onClick }: { icone: string; rotulo: string; onClick: () => void }) {
  return (
    <button type="button" className="cal-round cal-round--md" aria-label={rotulo} title={rotulo} onClick={onClick}>
      <i className={icone} aria-hidden />
    </button>
  )
}

/**
 * Detalhe de um compromisso no popover (design do calendário, 2026-10-31):
 * evento do Orbind, marco de Item de Trabalho ou evento do Google. As ações
 * ficam no topo, em ícones; o resto são linhas com ícone e texto.
 */
export function DetalheDoCompromisso({
  c,
  hoje,
  nomeDe,
  emailDoGoogle,
  onFechar,
  onEditar,
  onExcluir,
  onAbrirItem,
  onAbrirLink,
}: {
  c: Compromisso
  hoje: string
  nomeDe: (id: string) => string
  emailDoGoogle: string | null
  onFechar: () => void
  onEditar: (ev: EventoDaApi) => void
  onExcluir: (ev: EventoDaApi) => void
  onAbrirItem: (id: string) => void
  onAbrirLink: (url: string) => void
}) {
  const agenda = agendaInfo(c.agenda)
  const ev = c.evento
  const item = c.item
  const google = c.google ?? null
  const concluido = Boolean(item?.concluido)
  const reuniao = ev?.meetingUrl ?? google?.meetingUrl ?? null
  const linkDoGoogle = google?.htmlLink ?? ev?.googleLink ?? null
  const descricao = ev?.description ?? google?.description ?? ''
  const situacao = item ? situacaoDoItem(item, c.date, hoje) : null

  return (
    <div className="cal-sheet" style={{ '--cal-ev-cor': agenda.color } as CSSProperties}>
      <div className="cal-sheet__acoes">
        {ev?.podeEditar ? <Acao icone="fas fa-pen" rotulo="Editar" onClick={() => onEditar(ev)} /> : null}
        {ev?.podeEditar ? <Acao icone="far fa-trash-alt" rotulo="Excluir" onClick={() => onExcluir(ev)} /> : null}
        {item ? (
          <Acao icone="fas fa-external-link-alt" rotulo={`Abrir ${item.code}`} onClick={() => onAbrirItem(item.id)} />
        ) : null}
        {linkDoGoogle ? (
          <Acao icone="fab fa-google" rotulo="Abrir no Google Calendar" onClick={() => onAbrirLink(linkDoGoogle)} />
        ) : null}
        <Acao icone="fas fa-times" rotulo="Fechar (Esc)" onClick={onFechar} />
      </div>

      <div className="cal-sheet__cabeca">
        <span className="cal-sheet__cor" aria-hidden />
        <div className="cal-sheet__id">
          <h3 className={`cal-sheet__titulo${concluido ? ' cal-sheet__titulo--feito' : ''}`}>{c.title}</h3>
          <p className="cal-sheet__quando">{linhaDeQuando(c)}</p>
        </div>
      </div>

      {item && situacao ? (
        <>
          <Linha icone={item.marco === 'inicio' ? 'fas fa-play-circle' : 'fas fa-flag'}>
            {item.marco === 'inicio' ? 'Início do Item de Trabalho' : 'Entrega do Item de Trabalho'}
          </Linha>
          <Linha icone="fas fa-link">
            <button type="button" className="cal-sheet__link" onClick={() => onAbrirItem(item.id)}>
              {item.code}
            </button>
          </Linha>
          <Linha icone="fas fa-tasks">
            <span className={`cal-situacao cal-situacao--${situacao.tom}`}>
              {situacao.tom === 'success' ? <i className="fas fa-check-circle" aria-hidden /> : null}
              {situacao.texto}
            </span>
          </Linha>
          <Linha icone="far fa-user">{item.assigneeMemberId ? nomeDe(item.assigneeMemberId) : 'Sem responsável'}</Linha>
        </>
      ) : null}

      {reuniao ? (
        <Linha icone="fas fa-video" alto>
          <button type="button" className="cal-sheet__reuniao" onClick={() => onAbrirLink(reuniao)}>
            {ehGoogleMeet(reuniao) ? 'Participar com Google Meet' : 'Entrar na reunião'}
          </button>
        </Linha>
      ) : null}

      {ev ? (
        <>
          <Linha icone="fas fa-user-friends">
            {ev.memberIds.length === 0 ? 'Ninguém convidado' : ev.memberIds.map(nomeDe).join(', ')}
          </Linha>
          {ev.location ? <Linha icone="fas fa-map-marker-alt">{ev.location}</Linha> : null}
          <Linha icone="far fa-user">Criado por {nomeDe(ev.ownerMemberId)}</Linha>
        </>
      ) : google?.location ? (
        <Linha icone="fas fa-map-marker-alt">{google.location}</Linha>
      ) : null}

      {descricao ? (
        <Linha icone="fas fa-align-left">
          <span className="cal-sheet__descricao">{descricao}</span>
        </Linha>
      ) : null}

      <Linha icone="far fa-calendar">
        <span className="cal-sheet__agenda">
          <i className={agenda.icon} aria-hidden /> {agenda.label}
        </span>
        {google && emailDoGoogle ? <span className="cal-sheet__sub">{emailDoGoogle}</span> : null}
        {ev?.googleLink ? (
          <span className="cal-sheet__sub">Também no seu Google Calendar · apagar lá apaga aqui também</span>
        ) : null}
      </Linha>

      {item ? (
        <p className="cal-sheet__nota">
          Este compromisso vem do Item de Trabalho. Para mudar datas ou status, edite o item no Orbind.
        </p>
      ) : google ? (
        <p className="cal-sheet__nota">Evento do seu Google Calendar — para mudar, abra no Google.</p>
      ) : null}
    </div>
  )
}
